import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { PageContainer } from '../components/Container/PageContainer';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Avatar,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// Removed icon publish/unpublish in favor of Switch toggle UI
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LinearProgress } from '@mui/material';
import { getAllCollection } from '../utils/pureDataFetch';
import {
  createIssue,
  subscribeIssues,
  subscribeIssueItems,
  addCollectionsToIssue,
  removeItemFromIssue,
  reorderItems,
  publishIssue,
  updateIssueMeta,
  recomputeIssueUserList,
  setCoverStories,
  addSection,
  updateSection,
  removeSection,
  setSectionThreads,
  reorderSections,
} from '../services/adminMagazineManager';
import axios from 'axios';

const AdminMonthlyMagazine = () => {
  const [issues, setIssues] = useState([]);
  const [selectedIssueId, setSelectedIssueId] = useState('');
  const [issueItems, setIssueItems] = useState([]);
  const [creatingIssue, setCreatingIssue] = useState(false);

  // collections search/pagination
  const [collections, setCollections] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [loadingCollections, setLoadingCollections] = useState(false);

  // details maps
  const [collectionInfoById, setCollectionInfoById] = useState({}); // { [id]: docData }
  const [userInfoById, setUserInfoById] = useState({}); // { [userId]: docData }

  // issue meta local states
  const [metaTitle, setMetaTitle] = useState('');
  const [metaCoverImage, setMetaCoverImage] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaNextTitle, setMetaNextTitle] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  // thread map built from collections in this issue
  const [threadInfoById, setThreadInfoById] = useState({});

  // thread picker dialog state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCollectionId, setPickerCollectionId] = useState('');
  const [pickerMode, setPickerMode] = useState('cover'); // 'cover' | 'section'
  const [pickerSectionId, setPickerSectionId] = useState('');
  const [pickerFilter, setPickerFilter] = useState('');

  useEffect(() => {
    const unsub = subscribeIssues(
      (list) => {
        setIssues(list);
        setSelectedIssueId((prev) => {
          if (prev && list.some((i) => i.id === prev)) return prev;
          return list.length > 0 ? list[0].id : '';
        });
      },
      (e) => console.error('issues subscribe error', e)
    );
    return () => unsub && unsub();
  }, []);

  useEffect(() => {
    if (!selectedIssueId) return;
    const unsub = subscribeIssueItems(
      selectedIssueId,
      (items) => setIssueItems(items),
      (e) => console.error('items subscribe error', e)
    );
    return () => unsub && unsub();
  }, [selectedIssueId]);

  // fetch details for current issue items (collections and users)
  useEffect(() => {
    const refreshDetails = async () => {
      try {
        // dedupe ids
        const colIds = Array.from(new Set(issueItems.map((it) => it.collectionId).filter(Boolean)));
        const userIds = Array.from(new Set(issueItems.map((it) => it.userId).filter(Boolean)));

        // fetch collections not in cache
        const missingColIds = colIds.filter((id) => !collectionInfoById[id]);
        if (missingColIds.length > 0) {
          const snaps = await Promise.all(
            missingColIds.map((id) =>
              import('../firebaseInstance').then(({ dbService }) => dbService.collection('collection').doc(id).get())
            )
          );
          setCollectionInfoById((prev) => {
            const next = { ...prev };
            snaps.forEach((snap, idx) => {
              const id = missingColIds[idx];
              if (snap.exists) next[id] = { id, ...snap.data() };
            });
            return next;
          });
        }

        // fetch users not in cache
        const missingUserIds = userIds.filter((id) => !userInfoById[id]);
        if (missingUserIds.length > 0) {
          const snaps = await Promise.all(
            missingUserIds.map((id) =>
              import('../firebaseInstance').then(({ dbService }) => dbService.collection('user').doc(id).get())
            )
          );
          setUserInfoById((prev) => {
            const next = { ...prev };
            snaps.forEach((snap, idx) => {
              const id = missingUserIds[idx];
              if (snap.exists) next[id] = { id, ...snap.data() };
            });
            return next;
          });
        }
      } catch (e) {
        console.error('refreshDetails error', e);
      }
    };
    if (issueItems && issueItems.length > 0) {
      refreshDetails();
    }
  }, [issueItems, collectionInfoById, userInfoById]);

  // build threadInfoById from fetched collections' threadList
  useEffect(() => {
    try {
      const map = {};
      Object.values(collectionInfoById || {}).forEach((col) => {
        const list = Array.isArray(col?.threadList) ? col.threadList : [];
        list.forEach((th) => {
          if (!th || !th.id) return;
          map[String(th.id)] = { ...th, collectionId: col.id };
        });
      });
      setThreadInfoById(map);
    } catch (e) {
      // ignore
    }
  }, [collectionInfoById]);

  // collections fetch, with client-side search & pagination
  const loadCollections = useCallback(async () => {
    setLoadingCollections(true);
    try {
      const all = await getAllCollection();
      setCollections(all);
    } catch (e) {
      console.error('loadCollections error', e);
    } finally {
      setLoadingCollections(false);
    }
  }, []);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const filteredCollections = useMemo(() => {
    const keyword = String(search || '').toLowerCase();
    const includedSet = new Set(issueItems.map((it) => it.collectionId));
    return (collections || [])
      .filter((c) => (c.title || '').toLowerCase().includes(keyword))
      .map((c) => ({ ...c, isIncluded: includedSet.has(c.id) }));
  }, [collections, search, issueItems]);

  const pagedCollections = useMemo(() => {
    const start = page * pageSize;
    return filteredCollections.slice(start, start + pageSize);
  }, [filteredCollections, page, pageSize]);

  const handleCreateThisMonth = async () => {
    if (creatingIssue) return;
    setCreatingIssue(true);
    try {
      const now = new Date();
      const issueId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      await createIssue(issueId, {});
      setSelectedIssueId(issueId);
    } catch (e) {
      console.error('create issue failed', e);
      alert('이슈 생성 실패');
    } finally {
      setCreatingIssue(false);
    }
  };

  const handleAddCollection = async (collection) => {
    if (!selectedIssueId) return;
    try {
      await addCollectionsToIssue(selectedIssueId, [{ id: collection.id, userID: collection.userID }]);
      // force details refresh for newly added item
      setTimeout(() => {
        setCollectionInfoById((prev) => ({ ...prev }));
        setUserInfoById((prev) => ({ ...prev }));
      }, 0);
    } catch (e) {
      console.error('add collection failed', e);
      alert('컬렉션 추가 실패');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!selectedIssueId) return;
    try {
      await removeItemFromIssue(selectedIssueId, itemId);
    } catch (e) {
      console.error('remove item failed', e);
      alert('아이템 제거 실패');
    }
  };

  const moveItem = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= issueItems.length) return;
    const newOrder = [...issueItems];
    const tmp = newOrder[index];
    newOrder[index] = newOrder[target];
    newOrder[target] = tmp;
    try {
      await reorderItems(selectedIssueId, newOrder.map((it) => it.id));
    } catch (e) {
      console.error('reorder failed', e);
      alert('정렬 실패');
    }
  };

  const currentIssue = useMemo(() => issues.find((i) => i.id === selectedIssueId), [issues, selectedIssueId]);

  const participants = useMemo(() => {
    // 1) root userIdList 우선 사용
    const rootList = Array.isArray(currentIssue?.userIdList) ? currentIssue.userIdList : null;
    const ids = rootList && rootList.length > 0
      ? rootList.map((v) => String(v))
      : Array.from(new Set(issueItems.map((it) => it.userId).filter(Boolean).map((v) => String(v))));
    return ids.map((uid) => userInfoById[uid] || { id: uid });
  }, [issueItems, currentIssue, userInfoById]);

  const togglePublish = async () => {
    if (!currentIssue) return;
    try {
      await publishIssue(currentIssue.id, !currentIssue.isPublished);
    } catch (e) {
      console.error('publish toggle failed', e);
      alert('발행 상태 변경 실패');
    }
  };

  // sync meta state when issue changes
  useEffect(() => {
    if (!currentIssue) {
      setMetaTitle('');
      setMetaCoverImage('');
      setMetaDescription('');
      setMetaNextTitle('');
      return;
    }
    setMetaTitle(currentIssue.title || '');
    setMetaCoverImage(currentIssue.coverImage || '');
    setMetaDescription(currentIssue.description || '');
    setMetaNextTitle(currentIssue.nextTitle || '');
  }, [currentIssue]);

  const handleSaveMeta = async () => {
    if (!currentIssue) return;
    setSavingMeta(true);
    try {
      await updateIssueMeta(currentIssue.id, {
        title: String(metaTitle || '').trim(),
        coverImage: String(metaCoverImage || '').trim() || null,
        description: String(metaDescription || '').trim() || null,
        nextTitle: String(metaNextTitle || '').trim() || null,
      });
    } catch (e) {
      console.error('save meta failed', e);
      alert('메타데이터 저장 실패');
    } finally {
      setSavingMeta(false);
    }
  };

  // Cover stories helpers
  const coverList = Array.isArray(currentIssue?.coverStoryList)
    ? currentIssue.coverStoryList.map((v) => String(v))
    : [];

  const handleCoverAdd = async (threadId) => {
    if (!currentIssue) return;
    const base = Array.isArray(currentIssue.coverStoryList)
      ? currentIssue.coverStoryList.map((v) => String(v))
      : [];
    const next = Array.from(new Set([...base, String(threadId)]));
    await setCoverStories(currentIssue.id, next);
  };

  const handleCoverRemove = async (threadId) => {
    if (!currentIssue) return;
    const base = Array.isArray(currentIssue.coverStoryList)
      ? currentIssue.coverStoryList.map((v) => String(v))
      : [];
    const next = base.filter((id) => String(id) !== String(threadId));
    await setCoverStories(currentIssue.id, next);
  };

  const moveCover = async (index, delta) => {
    if (!currentIssue) return;
    const base = Array.isArray(currentIssue.coverStoryList)
      ? currentIssue.coverStoryList.map((v) => String(v))
      : [];
    const target = index + delta;
    if (target < 0 || target >= base.length) return;
    const re = [...base];
    const tmp = re[index];
    re[index] = re[target];
    re[target] = tmp;
    await setCoverStories(currentIssue.id, re);
  };

  // Sections helpers
  const sections = useMemo(() => {
    const list = Array.isArray(currentIssue?.sectionList) ? currentIssue.sectionList : [];
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [currentIssue]);

  // section drafts for editing existing sections
  const [sectionDraftsById, setSectionDraftsById] = useState({});

  useEffect(() => {
    // merge new sections into drafts without clobbering ongoing edits; remove deleted ones
    setSectionDraftsById((prev) => {
      const next = {};
      sections.forEach((s) => {
        const existing = prev[s.id];
        next[s.id] = existing || { title: s.title || '', description: s.description || '' };
      });
      return next;
    });
  }, [sections]);

  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionDesc, setNewSectionDesc] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadProgress, setCoverUploadProgress] = useState(0);
  const [sectionUploadingById, setSectionUploadingById] = useState({});
  const [sectionUploadProgressById, setSectionUploadProgressById] = useState({});

  const handleCreateSection = async () => {
    if (!currentIssue) return;
    const id = await addSection(currentIssue.id, { title: newSectionTitle, description: newSectionDesc });
    if (id) {
      setNewSectionTitle('');
      setNewSectionDesc('');
    }
  };

  const handleSectionSave = async (section) => {
    if (!currentIssue) return;
    const draft = sectionDraftsById[section.id] || {};
    await updateSection(currentIssue.id, section.id, {
      title: String(draft.title || '').trim(),
      description: String(draft.description || '').trim(),
    });
  };

  const handleSectionRevert = (section) => {
    setSectionDraftsById((prev) => ({
      ...prev,
      [section.id]: {
        title: section.title || '',
        description: section.description || '',
      },
    }));
  };

  const handleSectionRemove = async (sectionId) => {
    if (!currentIssue) return;
    await removeSection(currentIssue.id, sectionId);
  };

  const moveSection = async (index, delta) => {
    if (!currentIssue) return;
    const list = sections.map((s) => s.id);
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const re = [...list];
    const tmp = re[index];
    re[index] = re[target];
    re[target] = tmp;
    await reorderSections(currentIssue.id, re);
  };

  const handleSectionAddThread = async (sectionId, threadId) => {
    if (!currentIssue) return;
    const sec = sections.find((s) => s.id === sectionId);
    const base = Array.isArray(sec?.threadIdList) ? sec.threadIdList.map((v) => String(v)) : [];
    const next = Array.from(new Set([...base, String(threadId)]));
    await setSectionThreads(currentIssue.id, sectionId, next);
  };

  const handleSectionRemoveThread = async (sectionId, threadId) => {
    if (!currentIssue) return;
    const sec = sections.find((s) => s.id === sectionId);
    const base = Array.isArray(sec?.threadIdList) ? sec.threadIdList.map((v) => String(v)) : [];
    const next = base.filter((id) => String(id) !== String(threadId));
    await setSectionThreads(currentIssue.id, sectionId, next);
  };

  const handleUploadCoverVideo = async (file) => {
    if (!currentIssue || !file) return;
    setCoverUploading(true);
    setCoverUploadProgress(0);
    try {
      const form = new FormData();
      form.append('issueId', currentIssue.id);
      form.append('file', file);
      await axios.post('/v1/upload/video/cover', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setCoverUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      alert('커버 영상 업로드 완료');
    } catch (e) {
      console.error('cover upload failed', e);
      alert('커버 영상 업로드 실패');
    } finally {
      setCoverUploading(false);
      setCoverUploadProgress(0);
    }
  };

  const handleUploadSectionVideo = async (section, index, file) => {
    if (!currentIssue || !file || !section) return;
    const secId = section.id;
    setSectionUploadingById((prev) => ({ ...prev, [secId]: true }));
    setSectionUploadProgressById((prev) => ({ ...prev, [secId]: 0 }));
    try {
      const form = new FormData();
      form.append('issueId', currentIssue.id);
      form.append('sectionIndex', String(index));
      form.append('file', file);
      await axios.post('/v1/upload/video/section', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) {
            const p = Math.round((e.loaded / e.total) * 100);
            setSectionUploadProgressById((prev) => ({ ...prev, [secId]: p }));
          }
        },
      });
      alert(`섹션 ${index + 1} 영상 업로드 완료`);
    } catch (e) {
      console.error('section upload failed', e);
      alert('섹션 영상 업로드 실패');
    } finally {
      setSectionUploadingById((prev) => ({ ...prev, [secId]: false }));
      setSectionUploadProgressById((prev) => ({ ...prev, [secId]: 0 }));
    }
  };

  // Thread Picker Dialog
  const openPicker = (mode, opts = {}) => {
    setPickerMode(mode || 'cover');
    setPickerSectionId(opts.sectionId || '');
    setPickerCollectionId(opts.collectionId || '');
    setPickerFilter('');
    setPickerOpen(true);
  };
  const closePicker = () => setPickerOpen(false);

  const pickerThreads = useMemo(() => {
    const all = Object.values(threadInfoById || {});
    const filteredByCollection = pickerCollectionId ? all.filter((t) => t.collectionId === pickerCollectionId) : all;
    const keyword = String(pickerFilter || '').toLowerCase();
    const filtered = keyword
      ? filteredByCollection.filter((t) => String(t.title || t.text || t.id).toLowerCase().includes(keyword))
      : filteredByCollection;
    return filtered.sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }, [threadInfoById, pickerCollectionId, pickerFilter]);

  const handlePickerSelect = async (threadId) => {
    if (!currentIssue) return;
    if (pickerMode === 'cover') {
      await handleCoverAdd(threadId);
    } else if (pickerMode === 'section' && pickerSectionId) {
      await handleSectionAddThread(pickerSectionId, threadId);
    }
  };

  const handleDownloadIssueThreadsJson = async () => {
    if (!currentIssue) {
      alert('이슈를 먼저 선택하세요.');
      return;
    }
    try {
      // Collect required ids
      const colIds = Array.from(new Set(issueItems.map((it) => it.collectionId).filter(Boolean)));
      const userIds = Array.from(new Set(issueItems.map((it) => it.userId).filter(Boolean)));

      // Prepare local maps seeded from state
      const localCollectionMap = { ...collectionInfoById };
      const localUserMap = { ...userInfoById };

      // Fetch missing collections
      const missingColIds = colIds.filter((id) => !localCollectionMap[id]);
      if (missingColIds.length > 0) {
        const colSnaps = await Promise.all(
          missingColIds.map((id) =>
            import('../firebaseInstance').then(({ dbService }) => dbService.collection('collection').doc(id).get())
          )
        );
        colSnaps.forEach((snap, idx) => {
          const id = missingColIds[idx];
          if (snap.exists) localCollectionMap[id] = { id, ...snap.data() };
        });
        // update cache (non-blocking for export building)
        setCollectionInfoById((prev) => {
          const next = { ...prev };
          colSnaps.forEach((snap, idx) => {
            const id = missingColIds[idx];
            if (snap.exists) next[id] = { id, ...snap.data() };
          });
          return next;
        });
      }

      // Fetch missing users
      const missingUserIds = userIds.filter((id) => !localUserMap[id]);
      if (missingUserIds.length > 0) {
        const userSnaps = await Promise.all(
          missingUserIds.map((id) =>
            import('../firebaseInstance').then(({ dbService }) => dbService.collection('user').doc(id).get())
          )
        );
        userSnaps.forEach((snap, idx) => {
          const id = missingUserIds[idx];
          if (snap.exists) localUserMap[id] = { id, ...snap.data() };
        });
        // update cache (non-blocking for export building)
        setUserInfoById((prev) => {
          const next = { ...prev };
          userSnaps.forEach((snap, idx) => {
            const id = missingUserIds[idx];
            if (snap.exists) next[id] = { id, ...snap.data() };
          });
          return next;
        });
      }

      const sanitizeThread = (th) => {
        if (!th) return null;
        const id = th.id != null ? String(th.id) : undefined;
        const title = th.title != null ? String(th.title) : undefined;
        const text = th.text != null ? String(th.text) : undefined;
        const createdAt = th.createdAt != null ? th.createdAt : th.timestamp != null ? th.timestamp : undefined;
        const likeCount = th.likeCount != null ? th.likeCount : th.likes != null ? th.likes : undefined;
        const replyCount = th.replyCount != null ? th.replyCount : th.replies != null ? th.replies : undefined;
        const media = th.media != null ? th.media : th.imageUrls != null ? th.imageUrls : undefined;
        const obj = { id, title, text, createdAt, likeCount, replyCount, media };
        return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
      };

      // Group by user -> collections
      const userToCollections = {};
      issueItems.forEach((it) => {
        const uid = String(it.userId || '');
        const colId = it.collectionId;
        if (!uid || !colId) return;
        const col = localCollectionMap[colId];
        if (!col) return;
        if (!userToCollections[uid]) {
          userToCollections[uid] = {};
        }
        if (!userToCollections[uid][colId]) {
          const threadList = Array.isArray(col.threadList) ? col.threadList : [];
          userToCollections[uid][colId] = {
            id: col.id,
            title: col.title || '',
            description: col.description || col.summary || '',
            isPublic: col.isPublic !== false,
            threads: threadList.map(sanitizeThread).filter(Boolean),
          };
        }
      });

      const exportData = {
        issue: {
          id: currentIssue.id,
          title: currentIssue.title || '',
          description: currentIssue.description || '',
        },
        users: participants.map((p) => {
          const uid = String(p.id || '');
          const profile = p?.profile || {};
          const collectionsMap = userToCollections[uid] || {};
          return {
            userId: uid,
            username: profile.username || '',
            displayName: profile.name || '',
            profileImage: profile.threads_profile_picture_url || '',
            collections: Object.values(collectionsMap),
          };
        }),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `issue-${currentIssue.id}-threads.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('download export failed', e);
      alert('다운로드 실패');
    }
  };

  const handleDownloadSectionsJson = async () => {
    if (!currentIssue) {
      alert('이슈를 먼저 선택하세요.');
      return;
    }
    try {
      // Ensure collections are locally available to build thread map
      const colIds = Array.from(new Set(issueItems.map((it) => it.collectionId).filter(Boolean)));
      const localCollectionMap = { ...collectionInfoById };
      const missingColIds = colIds.filter((id) => !localCollectionMap[id]);
      if (missingColIds.length > 0) {
        const colSnaps = await Promise.all(
          missingColIds.map((id) =>
            import('../firebaseInstance').then(({ dbService }) => dbService.collection('collection').doc(id).get())
          )
        );
        colSnaps.forEach((snap, idx) => {
          const id = missingColIds[idx];
          if (snap.exists) localCollectionMap[id] = { id, ...snap.data() };
        });
        setCollectionInfoById((prev) => {
          const next = { ...prev };
          colSnaps.forEach((snap, idx) => {
            const id = missingColIds[idx];
            if (snap.exists) next[id] = { id, ...snap.data() };
          });
          return next;
        });
      }

      // Build local thread map from collections
      const localThreadMap = {};
      Object.values(localCollectionMap).forEach((col) => {
        const list = Array.isArray(col?.threadList) ? col.threadList : [];
        list.forEach((th) => {
          if (!th || !th.id) return;
          localThreadMap[String(th.id)] = { ...th, collectionId: col.id };
        });
      });

      const sanitizeThread = (th) => {
        if (!th) return null;
        const id = th.id != null ? String(th.id) : undefined;
        const title = th.title != null ? String(th.title) : undefined;
        const text = th.text != null ? String(th.text) : undefined;
        const createdAt = th.createdAt != null ? th.createdAt : th.timestamp != null ? th.timestamp : undefined;
        const likeCount = th.likeCount != null ? th.likeCount : th.likes != null ? th.likes : undefined;
        const replyCount = th.replyCount != null ? th.replyCount : th.replies != null ? th.replies : undefined;
        const media = th.media != null ? th.media : th.imageUrls != null ? th.imageUrls : undefined;
        const obj = { id, title, text, createdAt, likeCount, replyCount, media };
        return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
      };

      const sectionsData = sections.map((sec) => {
        const ids = Array.isArray(sec.threadIdList) ? sec.threadIdList : [];
        const threads = ids
          .map((tid) => sanitizeThread(localThreadMap[String(tid)] || { id: tid }))
          .filter(Boolean);
        return {
          id: sec.id,
          title: sec.title || '',
          description: sec.description || '',
          order: sec.order ?? 0,
          threads,
        };
      });

      const exportData = {
        issue: {
          id: currentIssue.id,
          title: currentIssue.title || '',
          description: currentIssue.description || '',
        },
        sections: sectionsData,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `issue-${currentIssue.id}-sections.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('download sections export failed', e);
      alert('다운로드 실패');
    }
  };

  return (
    <PageContainer>
      <Stack spacing={2}>
        <Typography variant="h4">Monthly Threads (Admin)</Typography>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="issue-select-label">이슈 선택</InputLabel>
              <Select
                labelId="issue-select-label"
                label="이슈 선택"
                value={selectedIssueId || ''}
                onChange={(e) => setSelectedIssueId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>이슈를 선택하세요</MenuItem>
                {issues.map((iss) => (
                  <MenuItem key={iss.id} value={iss.id}>
                    {iss.title || iss.id} {iss.isPublished ? '✅' : '📝'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={handleCreateThisMonth} disabled={creatingIssue}>
              이번 달 이슈 생성
            </Button>
            {currentIssue && (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(currentIssue.isPublished)}
                    onChange={async (e) => {
                      try {
                        await publishIssue(currentIssue.id, e.target.checked);
                      } catch (err) {
                        console.error('publish toggle failed', err);
                        alert('발행 상태 변경 실패');
                      }
                    }}
                    color="primary"
                  />
                }
                label={Boolean(currentIssue.isPublished) ? '발행됨' : '비공개'}
              />
            )}
            {currentIssue && (
              <Button
                startIcon={<FileDownloadIcon />}
                variant="contained"
                color="primary"
                onClick={handleDownloadIssueThreadsJson}
              >
                JSON 다운로드
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Collections Add Panel */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Typography variant="h6" sx={{ flex: 1 }}>컬렉션 추가</Typography>
            <TextField
              label="컬렉션 검색"
              size="small"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="제목 키워드"
            />
            <Typography variant="body2" color="text.secondary">
              총 {filteredCollections.length}개 / 페이지 {page + 1}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          {loadingCollections ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            <Stack spacing={1}>
              {pagedCollections.map((c) => (
                <Paper key={c.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }} variant="outlined">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{c.title || 'Untitled'} {c.isPublic === false ? '(Private)' : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">userId: {c.userID || 'Unknown'} / id: {c.id}</Typography>
                  </Box>
                  {c.isIncluded ? (
                    <Chip label="추가됨" color="success" size="small" />
                  ) : (
                    <Button size="small" variant="contained" onClick={() => handleAddCollection(c)}>추가</Button>
                  )}
                </Paper>
              ))}
              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button size="small" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>이전</Button>
                <Button size="small" disabled={(page + 1) * pageSize >= filteredCollections.length} onClick={() => setPage((p) => p + 1)}>다음</Button>
              </Stack>
            </Stack>
          )}
        </Paper>

        {/* Issue Meta Editor */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>이슈 메타데이터</Typography>
          {!currentIssue ? (
            <Typography variant="body2" color="text.secondary">이슈를 먼저 선택하세요.</Typography>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="제목"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="커버 이미지 URL"
                value={metaCoverImage}
                onChange={(e) => setMetaCoverImage(e.target.value)}
                size="small"
                fullWidth
                placeholder="https://..."
              />
              {metaCoverImage ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Avatar src={metaCoverImage} sx={{ width: 56, height: 56 }} />
                  <Typography variant="caption" color="text.secondary">미리보기</Typography>
                </Box>
              ) : null}
              <TextField
                label="설명"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={3}
              />
              <TextField
                label="다음 매거진 주제"
                value={metaNextTitle}
                onChange={(e) => setMetaNextTitle(e.target.value)}
                size="small"
                fullWidth
                placeholder="예: 크리에이터의 일상, 스타트업 이야기, 트렌드 분석 등"
                helperText="다음 호에서 다룰 주제를 입력하세요"
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={savingMeta} onClick={handleSaveMeta}>저장</Button>
                <Button variant="text" disabled={savingMeta} onClick={() => {
                  setMetaTitle(currentIssue.title || '');
                  setMetaCoverImage(currentIssue.coverImage || '');
                  setMetaDescription(currentIssue.description || '');
                  setMetaNextTitle(currentIssue.nextTitle || '');
                }}>되돌리기</Button>
              </Stack>
              <Divider />
              <Typography variant="subtitle2">커버 영상 업로드</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Button variant="outlined" component="label" disabled={coverUploading}>
                  파일 선택 및 업로드
                  <input
                    hidden
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleUploadCoverVideo(f);
                      e.target.value = '';
                    }}
                  />
                </Button>
                {coverUploading && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 200 }}>
                    <LinearProgress variant="determinate" value={coverUploadProgress} sx={{ flex: 1, minWidth: 180 }} />
                    <Typography variant="caption" sx={{ width: 36, textAlign: 'right' }}>{coverUploadProgress}%</Typography>
                  </Stack>
                )}
                {!coverUploading && (
                  <Typography variant="caption" color="text.secondary">
                    업로드 경로: videos/monthly-cover-{currentIssue.id}.mp4
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>이슈 아이템</Typography>
          {issueItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">아직 추가된 컬렉션이 없습니다.</Typography>
          ) : (
            <Stack spacing={1}>
              {issueItems.map((it, idx) => {
                const col = collectionInfoById[it.collectionId];
                const usr = userInfoById[it.userId];
                const title = col?.title || 'Untitled';
                const isPublic = col?.isPublic ?? true;
                const username = usr?.profile?.username || usr?.profile?.name || it.userId;
                const avatarUrl = usr?.profile?.threads_profile_picture_url || '';
                return (
                  <Paper key={it.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }} variant="outlined">
                    <Chip label={`#${idx + 1}`} size="small" />
                    <Avatar src={avatarUrl} sx={{ width: 28, height: 28 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{title} {isPublic ? '' : '(Private)'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>@{username} · col:{it.collectionId}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Button size='small' variant='outlined' onClick={() => openPicker('cover', { collectionId: it.collectionId })}>스레드 보기</Button>
                      <IconButton size="small" onClick={() => moveItem(idx, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => moveItem(idx, +1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(it.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* Cover Stories Panel */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography variant='h6' sx={{ flex: 1 }}>커버 스토리</Typography>
            <Button size='small' variant='contained' onClick={() => openPicker('cover')}>스레드 추가</Button>
          </Stack>
          <Divider sx={{ my: 1 }} />
          {coverList.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>아직 추가된 커버 스토리가 없습니다.</Typography>
          ) : (
            <Stack spacing={1}>
              {coverList.map((tid, idx) => {
                const th = threadInfoById[tid] || { id: tid };
                const label = th?.title || th?.text || String(tid);
                return (
                  <Paper key={tid} sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }} variant='outlined'>
                    <Chip label={`#${idx + 1}`} size='small' />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant='body2' noWrap>{label}</Typography>
                      <Typography variant='caption' color='text.secondary' noWrap>threadId: {String(tid)}</Typography>
                    </Box>
                    <Stack direction='row' spacing={0.5}>
                      <IconButton size='small' onClick={() => moveCover(idx, -1)}><ArrowUpwardIcon fontSize='small' /></IconButton>
                      <IconButton size='small' onClick={() => moveCover(idx, +1)}><ArrowDownwardIcon fontSize='small' /></IconButton>
                      <IconButton size='small' color='error' onClick={() => handleCoverRemove(tid)}><DeleteIcon fontSize='small' /></IconButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* Sections Panel */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
            <Typography variant='h6' sx={{ flex: 1 }}>섹션 관리</Typography>
            <Button
              size='small'
              variant='contained'
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadSectionsJson}
            >
              섹션 JSON 다운로드
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
            <TextField label='섹션 제목' size='small' value={newSectionTitle} onChange={(e) => setNewSectionTitle(e.target.value)} />
            <TextField label='설명' size='small' value={newSectionDesc} onChange={(e) => setNewSectionDesc(e.target.value)} fullWidth />
            <Button variant='outlined' onClick={handleCreateSection}>섹션 추가</Button>
          </Stack>
          {sections.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>아직 생성된 섹션이 없습니다.</Typography>
          ) : (
            <Stack spacing={1}>
              {sections.map((sec, idx) => (
                <Paper key={sec.id} sx={{ p: 1.5 }} variant='outlined'>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                    <TextField
                      size='small'
                      label='제목'
                      value={sectionDraftsById[sec.id]?.title ?? ''}
                      onChange={(e) =>
                        setSectionDraftsById((prev) => ({
                          ...prev,
                          [sec.id]: { ...prev[sec.id], title: e.target.value },
                        }))
                      }
                      sx={{ minWidth: 220 }}
                    />
                    <TextField
                      size='small'
                      label='설명'
                      value={sectionDraftsById[sec.id]?.description ?? ''}
                      onChange={(e) =>
                        setSectionDraftsById((prev) => ({
                          ...prev,
                          [sec.id]: { ...prev[sec.id], description: e.target.value },
                        }))
                      }
                      fullWidth
                    />
                    <Stack direction='row' spacing={0.5}>
                      <IconButton size='small' onClick={() => moveSection(idx, -1)}><ArrowUpwardIcon fontSize='small' /></IconButton>
                      <IconButton size='small' onClick={() => moveSection(idx, +1)}><ArrowDownwardIcon fontSize='small' /></IconButton>
                      <Button size='small' variant='contained' onClick={() => handleSectionSave(sec)}>저장</Button>
                      <Button size='small' variant='text' onClick={() => handleSectionRevert(sec)}>되돌리기</Button>
                      <IconButton size='small' color='error' onClick={() => handleSectionRemove(sec.id)}><DeleteIcon fontSize='small' /></IconButton>
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mt: 1 }}>
                    <Button size='small' variant='outlined' component='label' disabled={Boolean(sectionUploadingById[sec.id])}>
                      섹션 영상 업로드
                      <input
                        hidden
                        type='file'
                        accept='video/*'
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) handleUploadSectionVideo(sec, idx, f);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    {sectionUploadingById[sec.id] ? (
                      <Stack direction='row' spacing={1} sx={{ alignItems: 'center', minWidth: 200 }}>
                        <LinearProgress
                          variant='determinate'
                          value={sectionUploadProgressById[sec.id] || 0}
                          sx={{ flex: 1, minWidth: 180 }}
                        />
                        <Typography variant='caption' sx={{ width: 36, textAlign: 'right' }}>
                          {sectionUploadProgressById[sec.id] || 0}%
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant='caption' color='text.secondary'>
                        업로드 경로: videos/sections/{currentIssue?.id}-s{idx + 1}.mp4
                      </Typography>
                    )}
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
                    <Typography variant='subtitle2' sx={{ flex: 1 }}>스레드</Typography>
                    <Button size='small' variant='contained' onClick={() => openPicker('section', { sectionId: sec.id })}>스레드 추가</Button>
                  </Stack>
                  <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                    {(sec.threadIdList || []).map((tid) => {
                      const th = threadInfoById[tid] || { id: tid };
                      const label = th?.title || th?.text || String(tid);
                      return (
                        <Chip
                          key={tid}
                          label={label}
                          onDelete={() => handleSectionRemoveThread(sec.id, tid)}
                          sx={{
                            maxWidth: 400,
                            overflow: 'hidden',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                              display: 'block',
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
      {/* Thread Picker Dialog */}
      <Dialog open={pickerOpen} onClose={closePicker} fullWidth maxWidth='md'>
        <DialogTitle>스레드 선택</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <TextField size='small' label='필터' value={pickerFilter} onChange={(e) => setPickerFilter(e.target.value)} />
              {pickerMode === 'section' && (
                <FormControl size='small' sx={{ minWidth: 220 }}>
                  <InputLabel id='section-select-label'>섹션 선택</InputLabel>
                  <Select labelId='section-select-label' label='섹션 선택' value={pickerSectionId} onChange={(e) => setPickerSectionId(e.target.value)}>
                    <MenuItem value='' disabled>섹션을 선택하세요</MenuItem>
                    {sections.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.title || 'Untitled'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
            <List sx={{ maxHeight: 420, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {pickerThreads.map((t) => {
                const label = t?.title || t?.text || String(t.id);
                const disabledBySection = pickerMode === 'section' && pickerSectionId
                  ? (sections.find((s) => s.id === pickerSectionId)?.threadIdList || []).map(String).includes(String(t.id))
                  : false;
                const disabledByCover = pickerMode === 'cover' ? coverList.map(String).includes(String(t.id)) : false;
                const disabled = disabledBySection || disabledByCover;
                return (
                  <ListItemButton key={t.id} disabled={disabled} onClick={() => handlePickerSelect(t.id)}>
                    <ListItemText
                      primary={label}
                      secondary={`threadId: ${String(t.id)} · collectionId: ${String(t.collectionId || '')}`}
                    />
                  </ListItemButton>
                );
              })}
              {pickerThreads.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant='body2' color='text.secondary'>표시할 스레드가 없습니다.</Typography>
                </Box>
              )}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePicker}>닫기</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AdminMonthlyMagazine;


